import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";

type MeetGreetRow = {
  id: string;
  pickupLocation: string;
  postCode: string;
  latitude: number | null;
  longitude: number | null;
  radiusMiles: number;
  charge: number;
  status: boolean;
  remarks: string | null;
  displayLocation: string;
};

const emptyForm = {
  pickupLocation: "",
  postCode: "",
  latitude: "",
  longitude: "",
  charge: "",
  status: true,
  remarks: "",
};

export default function MeetGreetCharges() {
  const [rows, setRows] = useState<MeetGreetRow[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [generalSettings, setGeneralSettings] = useState({
    generalCharge: "",
    status: true,
    remarks: "",
  });
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [chargesRes, settingsRes] = await Promise.all([
        axiosInstance.get<MeetGreetRow[]>("/meet-greet-charges"),
        axiosInstance.get<{
          generalCharge: number | null;
          status: boolean;
          remarks: string | null;
        }>("/meet-greet-charges/settings"),
      ]);
      setRows(chargesRes.data);
      setGeneralSettings({
        generalCharge:
          settingsRes.data.generalCharge != null
            ? String(settingsRes.data.generalCharge)
            : "",
        status: settingsRes.data.status ?? true,
        remarks: settingsRes.data.remarks ?? "",
      });
    } catch {
      setMessage("Failed to load meet & greet charges");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSaveGeneral = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingGeneral(true);
    setMessage("");
    try {
      await axiosInstance.put("/meet-greet-charges/settings", {
        generalCharge: generalSettings.generalCharge
          ? Number(generalSettings.generalCharge)
          : null,
        status: generalSettings.status,
        remarks: generalSettings.remarks || null,
      });
      setMessage("General meet & greet charge saved");
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Save failed";
      setMessage(msg);
    } finally {
      setSavingGeneral(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        pickupLocation: form.pickupLocation,
        postCode: form.postCode,
        latitude: form.latitude ? Number(form.latitude) : null,
        longitude: form.longitude ? Number(form.longitude) : null,
        radiusMiles: 0.2,
        charge: Number(form.charge),
        status: form.status,
        remarks: form.remarks || null,
      };

      if (editingId) {
        await axiosInstance.put(`/meet-greet-charges/${editingId}`, payload);
        setMessage("Meet & greet charge updated");
      } else {
        await axiosInstance.post("/meet-greet-charges", payload);
        setMessage("Meet & greet charge added");
      }
      resetForm();
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Save failed";
      setMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row: MeetGreetRow) => {
    setEditingId(row.id);
    setForm({
      pickupLocation: row.pickupLocation,
      postCode: row.postCode,
      latitude: row.latitude != null ? String(row.latitude) : "",
      longitude: row.longitude != null ? String(row.longitude) : "",
      charge: String(row.charge),
      status: row.status,
      remarks: row.remarks ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this meet & greet charge?")) return;
    await axiosInstance.delete(`/meet-greet-charges/${id}`);
    await load();
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageBreadcrumb pageTitle="Meet & Greet Charges" />
        <div className="flex gap-3 text-sm">
          <Link to="/vehicle-type" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
            Add Fleet Type
          </Link>
          <span className="text-gray-300">|</span>
          <a
            href="https://www.gov.uk/find-postcode"
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
          >
            Help
          </a>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 max-w-3xl">
        Set flat fees for airport pickups where the driver meets the customer inside the
        terminal. Location-specific charges apply within a <strong>0.2 mile</strong> radius.
        You can also set a <strong>general charge</strong> that applies to all locations when
        no specific location rule matches.
      </p>

      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h2 className="text-lg font-semibold text-gray-800">
          General meet &amp; greet charge (all locations)
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Used when meet &amp; greet is selected but the pickup does not match any location
          below. Location-specific charges take priority when they match.
        </p>

        <form onSubmit={handleSaveGeneral} className="mt-4 space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              General charge (£)
            </label>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">£</span>
              <input
                type="number"
                min="0"
                step="0.01"
                className="flex-1 border rounded-lg px-3 py-2"
                placeholder="e.g. 15.00"
                value={generalSettings.generalCharge}
                onChange={(e) =>
                  setGeneralSettings((s) => ({ ...s, generalCharge: e.target.value }))
                }
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={generalSettings.status}
              onChange={(e) =>
                setGeneralSettings((s) => ({ ...s, status: e.target.checked }))
              }
            />
            Active
          </label>

          <button
            type="submit"
            disabled={savingGeneral}
            className="px-5 py-2 bg-brand-500 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {savingGeneral ? "Saving..." : "Save general charge"}
          </button>
        </form>
      </section>

      <section className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h2 className="text-lg font-semibold text-gray-800">
          {editingId ? "Edit meet & greet location" : "Add meet & greet location"}
        </h2>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pickup location description
            </label>
            <input
              className="w-full border rounded-lg px-3 py-2"
              placeholder="e.g. T3 London Heathrow Airport LHR Terminal 3 - by Info desk in Arrivals"
              value={form.pickupLocation}
              onChange={(e) => setForm((f) => ({ ...f, pickupLocation: e.target.value }))}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postcode (for matching)
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2 uppercase"
                placeholder="TW6 1QG"
                value={form.postCode}
                onChange={(e) => setForm((f) => ({ ...f, postCode: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup charge (£)
              </label>
              <div className="flex items-center gap-1">
                <span className="text-gray-500">£</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="flex-1 border rounded-lg px-3 py-2"
                  value={form.charge}
                  onChange={(e) => setForm((f) => ({ ...f, charge: e.target.value }))}
                  required
                />
              </div>
            </div>
          </div>

          <details className="text-sm text-gray-600">
            <summary className="cursor-pointer text-blue-600">Optional coordinates (0.2 mi radius)</summary>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Latitude</label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.latitude}
                  onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Longitude</label>
                <input
                  type="number"
                  step="any"
                  className="w-full border rounded-lg px-3 py-2"
                  value={form.longitude}
                  onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Leave blank to match by postcode prefix only. Add coordinates for precise 0.2-mile
              radius matching at the terminal.
            </p>
          </details>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.checked }))}
            />
            Active
          </label>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 bg-brand-500 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? "Saving..." : editingId ? "Update" : "+ Add"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border rounded-lg text-gray-700"
              >
                Cancel
              </button>
            )}
          </div>
        </form>

        {message && (
          <p
            className={`mt-3 text-sm ${
              message.includes("Failed") || message.includes("failed")
                ? "text-red-600"
                : "text-green-600"
            }`}
          >
            {message}
          </p>
        )}
      </section>

      <section className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
        {loading ? (
          <p className="px-6 py-8 text-gray-500">Loading...</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">
                  Pickup location
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 w-32">
                  Pickup charge
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 w-28">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    No meet & greet charges configured yet.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50/80">
                    <td className="px-4 py-3 text-gray-800">
                      {row.displayLocation || `${row.pickupLocation} (${row.postCode})`}
                      {!row.status && (
                        <span className="ml-2 text-xs text-gray-400">(inactive)</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium">
                      £{Number(row.charge).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right space-x-3">
                      <div className="flex items-center justify-end gap-3">
                        <button
                        type="button"
                        className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
                        onClick={() => startEdit(row)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-600 hover:underline"
                        onClick={() => handleDelete(row.id)}
                      >
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
